import { calculateDistance, subtractCoordinates } from '../../utils/utils.js'

/**
 * 포인터 다운 이벤트 발생 시 제스처(멀티 터치) 관련 초기 설정을 처리하는 함수.
 * 이 함수는 제스처로 인한 캔버스 이동(translate) 또는 확대/축소(scope) 작업을 시작하기 위해
 * 필요한 초기값들을 계산하고, 진행 중인 다른 도구의 작업을 정리합니다.
 */
export const handleGestureDown = (e, state) => {
  const {
    moveActivePointsRef,
    previousMovePoint,
    translateAtPointerDown,
    translate,
    clientCoord,
    getBoundingClientSgCanvas,
    downCenterRef,
    downDistanceRef,
    downScopeRef,
    downTranslateRef,
    selectedTool,
    previousErasePoint,
    // 상태
    paths,
    currentPath,
    currentPathId,
    erasedPathKeys,
    scope,
    isDragging,
    isSelectedMovingActive,
    isResizeActive,
    // setter 함수
    setPaths,
    setIsMoving,
    setIsDragging,
    setIsSelectedMovingActive,
    setIsResizeActive,
    setIsErasingActive,
    // 동시편집
    yPathsRef,
  } = state

  // ─── 단일 터치 입력 (캔버스 이동 기능) ─────────────────────────────
  // 활성 포인터가 하나일 경우, 캔버스 이동 기능 활성화
  if (moveActivePointsRef.current.length <= 1) {
    setIsMoving(true)
    previousMovePoint.current = clientCoord
    translateAtPointerDown.current = { x: translate.x, y: translate.y }

    // 어떠한 tool에서든 마우스 휠 클릭으로 translate
    if (e.buttons === 4) return
  }

  // ─── 멀티 터치 입력 (확대/축소 제스처) ─────────────────────────────
  // 두 개 이상의 포인터가 활성인 경우, 확대/축소 제스처에 필요한 기준값을 설정함
  if (moveActivePointsRef.current.length >= 2) {
    // 두 포인터 간의 시간 차가 500ms를 초과하면 제스처를 취소하고 이동 기능을 해제
    if (moveActivePointsRef.current[1].time - moveActivePointsRef.current[0].time > 500) {
      setIsMoving(false)
      return
    }

    setIsMoving(true)

    let pointerDownClientCord = []
    pointerDownClientCord.push(
      subtractCoordinates(
        { x: moveActivePointsRef.current[0].x, y: moveActivePointsRef.current[0].y },
        getBoundingClientSgCanvas.current
      )
    )
    pointerDownClientCord.push(
      subtractCoordinates(
        { x: moveActivePointsRef.current[1].x, y: moveActivePointsRef.current[1].y },
        getBoundingClientSgCanvas.current
      )
    )

    // down 이벤트 정보: center/distance/scope/ref
    downCenterRef.current = {
      x: +((pointerDownClientCord[0].x + pointerDownClientCord[1].x) / 2).toFixed(2),
      y: +((pointerDownClientCord[0].y + pointerDownClientCord[1].y) / 2).toFixed(2),
    }
    downDistanceRef.current = calculateDistance(pointerDownClientCord[0], pointerDownClientCord[1])
    downScopeRef.current = scope
    downTranslateRef.current = translate

    /**
     * 제스처 진행 전 도구의 기능을 중단하고 상태를 초기화하는 부분
     *
     * - 선택 도구('select'): 드래그, 선택 이동, 리사이즈 상태를 모두 해제하여 초기 상태로 복원.
     * - 펜/도형 도구('pen-pressure', 'pen', 'pen-highlight', 'shape'): 진행 중인 경로 데이터를 삭제하고 관련 정보를 초기화.
     * - 지우개 도구('eraser'): 지우개 피드백 요소를 숨기고, 지우개 상태 및 이전 좌표를 초기화한 후,
     *   선택된 경로의 투명도를 복원하고 undo/redo 스택을 업데이트.
     */
    if (selectedTool === 'select') {
      if (isDragging) setIsDragging(false)
      if (isSelectedMovingActive) setIsSelectedMovingActive(false)
      if (isResizeActive) {
        setIsResizeActive(false)
      }
    }

    if (
      selectedTool === 'pen-pressure' ||
      selectedTool === 'pen' ||
      selectedTool === 'pen-highlight' ||
      selectedTool === 'shape'
    ) {
      // 진행 중인 경로 데이터 삭제 및 초기화
      delete paths[currentPathId.current]
      setPaths({ ...paths })

      // [동시편집 - 삭제]
      yPathsRef.current.delete(currentPathId.current)

      currentPath.current = null
      currentPathId.current = 0
    }

    if (selectedTool === 'eraser') {
      // eraser follower 숨기기
      const eraserElement = document.getElementById('eraser')
      eraserElement.style.display = 'none'

      setIsErasingActive(false)
      previousErasePoint.current = { x: 0, y: 0 }

      // 지워진 경로의 투명도 복원 후 undo/redo 스택 업데이트
      if (erasedPathKeys.current.length) {
        for (let erasedPathKey of erasedPathKeys.current) {
          const sgShapeDivElement = document.getElementById(`sg-shape-${erasedPathKey}`)
          sgShapeDivElement.style.opacity *= 4
        }
        erasedPathKeys.current = []
      }
    }

    return true
  }
  return false
}
